/**
 * Visitor tracking for the shop.
 *
 * Page views, dwell time and engagement (active time, attention time, scroll
 * depth, whether the visitor moved, scrolled, tapped or typed) come from the
 * shared trackhub tracker at track.cyberjosef.dev/t.js, which every site
 * loads. This file used to carry its own copy of that logic; the copies
 * drifted apart and one of them silently dropped every visitor's client
 * context, which is why there is now exactly one.
 *
 * `trackEvent` still lives here because it is shop-specific: it appends a
 * timestamped row to the visitor's journey and can move the funnel stage at
 * the same time. Beacons go to POST /track, which fans out to TrackVisitJob
 * (this app's own visitor tables) and to the central trackhub API.
 *
 * Affiliate attribution is deliberately absent from this file. /p/:slug sets a
 * cookie and the server reads it — if the browser could name its own partner,
 * anyone could post themselves into an affiliate's numbers.
 */

import { getFingerprint } from './fingerprint'

const TRACKER_SRC = import.meta.env.VITE_TRACKHUB_SCRIPT ?? 'https://track.cyberjosef.dev/t.js'

/** localStorage key for the visitor token. Unchanged, so returning visitors
 *  stay the same visitor rather than all looking new on deploy day. */
const TOKEN_KEY = 'sss_vid'
const FUNNEL_KEY = 'sss_funnel'

export type Stage =
  | 'visited'
  | 'clicked_cta'
  | 'added_to_cart'
  | 'reached_checkout'
  | 'filled_details'
  | 'purchased'

/** Must stay in sync with VisitorEvent::NAMES. */
export type EventName =
  | 'page_view'
  | 'cta_click'
  | 'add_to_cart'
  | 'buy_now'
  | 'remove_from_cart'
  | 'open_cart'
  | 'begin_checkout'
  | 'form_field'
  | 'place_order'
  | 'purchase'

type EventOptions = {
  /** Human-readable subject: product name, form field label, etc. */
  label?: string
  /** Page the event happened on. Defaults to the current pathname. */
  path?: string
  /** Extra structured detail (product id, cart value, ...). */
  meta?: Record<string, unknown>
  /** Advance the funnel stage alongside recording the event. */
  stage?: Stage
}

type TrackhubAPI = {
  __loaded?: boolean
  q?: unknown[][]
  pageview?: (path?: string) => void
  event?: (name: string, options?: { label?: string; path?: string; meta?: unknown }) => void
  fingerprint?: (value: string) => void
  set?: (fields: Record<string, unknown>) => void
}

declare global {
  interface Window {
    trackhub?: TrackhubAPI
  }
}

/**
 * Calls the shared tracker, queueing when the script has not landed yet.
 * Every path swallows its errors: tracking must never break the page.
 */
function call(method: string, ...args: unknown[]): void {
  if (typeof window === 'undefined') return

  try {
    if (!window.trackhub) window.trackhub = { q: [] }
    const api = window.trackhub

    if (api.__loaded) {
      const fn = (api as Record<string, unknown>)[method]
      if (typeof fn === 'function') (fn as (...a: unknown[]) => void)(...args)
      return
    }

    if (!api.q) api.q = []
    api.q.push([method, ...args])
  } catch {
    // Tracking must never break the page.
  }
}

/** Read `?funnel=` from the URL (persisting it), else the stored slug. */
function funnelSlug(): string | null {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('funnel')
    if (fromUrl) {
      localStorage.setItem(FUNNEL_KEY, fromUrl)
      return fromUrl
    }
    return localStorage.getItem(FUNNEL_KEY)
  } catch {
    return null
  }
}

/**
 * Append one event to the visitor's journey (and optionally move the stage).
 *
 * The stage travels inside `meta` rather than at the top level of the beacon:
 * the shared tracker has one event shape for every site and knows nothing
 * about this shop's funnel. TrackingController reads it back out and still
 * validates it against Visitor::STAGES, so a forged value changes nothing.
 */
export async function trackEvent(name: EventName, options: EventOptions = {}): Promise<void> {
  if (typeof window === 'undefined') return

  const meta: Record<string, unknown> = { ...(options.meta ?? {}) }
  if (options.stage) meta.stage = options.stage

  call('event', name, {
    label: options.label,
    path: options.path,
    meta: Object.keys(meta).length > 0 ? meta : undefined,
  })
}

// ---------------------------------------------------------------------------
// Ambient wiring
// ---------------------------------------------------------------------------

let started = false

/**
 * Load the shared tracker and hand it what only this app knows: the funnel
 * slug and the device fingerprint. Called once, from Tracker.tsx.
 */
export function startTracking(): void {
  if (typeof window === 'undefined' || started) return
  started = true

  // Queued before the script loads, so both reach the very first beacon.
  const funnel = funnelSlug()
  if (funnel) call('set', { funnel })
  void getFingerprint().then((fp) => {
    if (fp) call('fingerprint', fp)
  })

  if (document.querySelector('script[data-trackhub]')) return

  const script = document.createElement('script')
  script.src = TRACKER_SRC
  script.async = true
  script.dataset.trackhub = ''
  script.dataset.endpoint = '/track'
  script.dataset.key = TOKEN_KEY
  document.head.appendChild(script)
}

/**
 * Record that the visitor landed on `path`. The shared tracker already hooks
 * the history API that Inertia navigates through, so this is the explicit belt
 * to that braces; it de-duplicates by path, so a double call costs nothing.
 */
export function trackPageView(path = window.location.pathname): void {
  call('pageview', path)
}
